import { Controller } from "@hotwired/stimulus"

// Keeps a table's column headings in view once its own heading row has
// scrolled up under the page's sticky heading.
//
// The table scrolls sideways inside its box (.ad-scroll), so a wide table
// does not push the page out. A heading cannot stick to the window from
// inside a box that scrolls, so a copy of the heading row is fixed to the top
// while it is needed, with the widths and the sideways scroll of the real
// one. The copy is hidden from screen readers and from Tab: the real headings
// are still in the table. A sort link in it can still be clicked.
export default class extends Controller {
  connect() {
    this.table = this.element.querySelector("table")
    this.head = this.table?.tHead
    if (!this.head) return

    this.copy = this.buildCopy()
    this.schedule = this.schedule.bind(this)
    window.addEventListener("scroll", this.schedule, { passive: true })
    window.addEventListener("resize", this.schedule)
    this.element.addEventListener("scroll", this.schedule, { passive: true })
    this.update()
  }

  disconnect() {
    window.removeEventListener("scroll", this.schedule)
    window.removeEventListener("resize", this.schedule)
    this.element.removeEventListener("scroll", this.schedule)
    cancelAnimationFrame(this.frame)
    this.copy?.remove()
  }

  schedule() {
    cancelAnimationFrame(this.frame)
    this.frame = requestAnimationFrame(() => this.update())
  }

  buildCopy() {
    const copy = document.createElement("div")
    copy.className = "ad-stickyhead"
    copy.hidden = true
    copy.setAttribute("aria-hidden", "true")

    const table = document.createElement("table")
    table.className = this.table.className
    table.appendChild(this.head.cloneNode(true))
    copy.appendChild(table)

    // A tick box in the copy would tick nothing, and an id must not appear twice.
    copy.querySelectorAll("input, label").forEach((element) => element.remove())
    copy.querySelectorAll("[id]").forEach((element) => element.removeAttribute("id"))
    copy.querySelectorAll("a, button").forEach((element) => element.setAttribute("tabindex", "-1"))

    document.body.appendChild(copy)
    return copy
  }

  update() {
    const top = this.stuckAt
    const box = this.element.getBoundingClientRect()
    const head = this.head.getBoundingClientRect()
    const shown = head.height > 0 && head.top < top && box.bottom > top + head.height * 2

    this.copy.hidden = !shown
    if (!shown) return

    this.copy.style.top = `${top}px`
    this.copy.style.left = `${box.left}px`
    this.copy.style.width = `${this.element.clientWidth}px`

    const table = this.copy.firstElementChild
    table.style.width = `${this.table.offsetWidth}px`
    table.style.transform = `translateX(${-this.element.scrollLeft}px)`

    const widths = Array.from(this.head.rows[0].cells, (cell) => cell.getBoundingClientRect().width)
    Array.from(table.tHead.rows[0].cells).forEach((cell, index) => {
      cell.style.width = cell.style.minWidth = cell.style.maxWidth = `${widths[index]}px`
    })
  }

  // Where the copy goes: under the page's own heading, which sticks to the
  // top of the window and shrinks as the page scrolls, or at the very top
  // where a page has none.
  get stuckAt() {
    const pagehead = document.querySelector(".ad-pagehead")
    return pagehead ? Math.max(0, pagehead.getBoundingClientRect().bottom) : 0
  }
}
